/**
 * whiteboardImportService
 *
 * The whiteboard-photo analog of the Miro connector.
 *
 * Where the Miro integration reads a board's sticky notes, colors, and (x, y)
 * coordinates over the wire, this service reads the *same kinds of signals* out
 * of a **photo of a physical whiteboard**. A vision model is asked to recognize
 * the visual grammar people actually draw — boxes, sticky notes, circles,
 * arrows, lines, and containers — and return a structured scene graph. We then
 * map that scene to platform concepts exactly like Miro does:
 *
 *   • boxes / stickies / circles / text  → Items
 *   • sticky color                        → item category (problem, risk, …)
 *   • vertical position (altitude)        → item altitude level (top = strategic)
 *   • arrows                              → directed relationships (dependency)
 *   • plain lines                         → undirected "related" relationships
 *   • containers / regions                → "contains" relationships
 *
 * The mapping defaults below are the whiteboard equivalent of
 * DEFAULT_FIELD_MAPPINGS / the Miro color-mapping rules, and the preview step in
 * WhiteboardImportModal lets a user override any of them before committing.
 *
 * Transport: the same same-origin Anthropic proxy used by llmClient
 * (`/api/anthropic/v1/messages`), with a BYOK key from aiProviderKeys.
 */

import { aiKeyPreview, getAnthropicKeySource, isManagedAnthropicEnabled, personalAiFetch, providerHasApiKey } from './aiProviderKeys';
import { getSupabaseClient } from './supabaseClient';
import { resolveSignifierPatch } from './whiteboardSignifiers';
import type { MiroColorRule } from './fieldMappingRegistry';
import type { ItemLanguageType } from '../types/prioritization/lifecycle';
import type { AltitudeLevel } from '../types/prioritization/itemLifecycle';
import type {
  RelationshipEdgeType,
  RelationshipDirectionality,
} from '../types/prioritization/itemRelationships';

// ============================================================================
// Scene graph — what the vision model returns
// ============================================================================

export type WhiteboardNodeShape =
  | 'sticky'
  | 'box'
  | 'rounded_box'
  | 'circle'
  | 'diamond'
  | 'cloud'
  | 'text'
  | 'other';

export type WhiteboardEdgeKind =
  | 'arrow'        // single-headed arrow  → directed
  | 'double_arrow' // double-headed arrow  → bidirectional
  | 'dashed_arrow' // dashed single arrow  → directed, "soft"
  | 'line'         // plain connecting line → undirected
  | 'dashed_line'  // dashed line          → undirected, "soft"
  | 'other';

/** A recognized shape on the board (becomes a candidate Item). */
export interface WhiteboardNode {
  /** Model-assigned local reference, e.g. "n1". Stable within one analysis. */
  id: string;
  /** Transcribed text inside / labeling the shape. */
  text: string;
  shape: WhiteboardNodeShape;
  /**
   * Dominant/fill color name (yellow, blue, …) or 'unknown' for ink-on-white.
   * Kept as the primary color for backward compatibility; `fillColor` mirrors it.
   */
  color: string;
  /**
   * Separate color cues for a richer "visual language". A user can encode
   * different attributes in different channels — e.g. fill = impact, border =
   * risk, text = team. All normalized color names or undefined when not
   * distinguishable from the dominant color.
   */
  fillColor?: string;
  borderColor?: string;
  textColor?: string;
  /** Normalized position, origin top-left. x,y in [0,1]. */
  position: { x: number; y: number };
  /** id of a container/region node that visually encloses this node, if any. */
  containerId?: string | null;
  /** Region (lane/quadrant) this node sits in, when the board has them. */
  regionId?: string | null;
  /** True when the model thinks this shape is a grouping region, not a leaf. */
  isContainer?: boolean;
  /**
   * Model's confidence (0–1) in the text transcription — lower for messy
   * handwriting, glare, or ambiguous shapes. Drives the "needs review" flag.
   */
  confidence?: number;
  /**
   * A short anchor code (1–4 chars, e.g. "K7") written in a corner of the shape
   * to identify it across versions. Lets re-capture match a box exactly even
   * after it's been renamed or moved.
   */
  code?: string;
}

/** A recognized connector between two nodes (becomes a relationship). */
export interface WhiteboardEdge {
  id: string;
  sourceId: string;
  targetId: string;
  kind: WhiteboardEdgeKind;
  /**
   * Stroke color of the connector (same vocabulary as node colors), when the
   * model can distinguish it from black marker on white. Used by line_color /
   * arrow_color visual-language rules.
   */
  color?: string;
  /** Text written on / beside the connector, if any. */
  label?: string;
  /**
   * A short marker/word written ON the connector that signifies the KIND of
   * relationship (e.g. "⊃", "contains", "blocks", "needs", "related"), as
   * opposed to a longer descriptive `label`. Drives symbol→type mapping.
   */
  symbol?: string;
}

export interface WhiteboardPoint {
  x: number;
  y: number;
}

/**
 * Photo-space positions (normalized 0–1) of the four corner registration marks
 * drawn by the projection view. Present only when a projected board was
 * re-photographed; used to map recognized shapes into the board's coordinate
 * frame (see whiteboardRegistration).
 */
export interface SceneFiducials {
  topLeft: WhiteboardPoint;
  topRight: WhiteboardPoint;
  bottomRight: WhiteboardPoint;
  bottomLeft: WhiteboardPoint;
}

/**
 * Something on the board the model saw but could NOT confidently classify as a
 * node or a connector — a scribble, a doodle, illegible text, or an arrow with
 * unclear endpoints. Surfaced so the user decides what to do rather than having
 * it silently dropped.
 */
export interface WhiteboardUnrecognized {
  id: string;
  /** Short description of what was seen, e.g. "scribble", "illegible text". */
  description: string;
  /** Best-guess kind, to hint the user's options. */
  kind: 'shape' | 'connector' | 'text' | 'other';
  /** Approximate normalized position (top-left origin), when locatable. */
  position?: { x: number; y: number };
}

/**
 * A structural region drawn on the board: a swimlane, a quadrant of a 2×2,
 * or a legend/key box. Regions are STRUCTURE, not content — they never become
 * items; membership in one is spatial meaning (semantic-connectors 02-DESIGN).
 */
export type WhiteboardRegionKind = 'lane_horizontal' | 'lane_vertical' | 'quadrant' | 'legend';

export interface WhiteboardRegion {
  id: string;
  kind: WhiteboardRegionKind;
  /** The written lane/quadrant title or legend heading, when present. */
  title?: string;
  /** Normalized bounds, origin top-left. */
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface WhiteboardScene {
  title?: string;
  summary?: string;
  nodes: WhiteboardNode[];
  edges: WhiteboardEdge[];
  warnings: string[];
  /** Marks the model couldn't classify — the user decides what to do with each. */
  unrecognized?: WhiteboardUnrecognized[];
  /** Lanes / quadrants / legend boxes — board structure, never items. */
  regions?: WhiteboardRegion[];
  /** Corner registration marks, when a projected board was captured. */
  fiducials?: SceneFiducials;
}

// ============================================================================
// Signifier → platform mapping defaults
// ============================================================================

/**
 * Default sticky/ink color → item category. Mirrors the semantics used in the
 * Miro color-mapping editor so a team's color conventions carry over from
 * digital boards to physical ones. Unmapped colors leave category unset.
 */
export const DEFAULT_COLOR_TO_CATEGORY: Record<string, ItemLanguageType> = {
  orange: 'problem',
  red: 'risk',
  pink: 'commitment',
  green: 'opportunity',
  light_green: 'opportunity',
  blue: 'solution',
  light_blue: 'solution',
  cyan: 'solution',
  purple: 'investment',
  violet: 'investment',
  yellow: 'opportunity',
};

/** Default connector kind → relationship edge type + directionality. */
export const DEFAULT_EDGE_TO_RELATIONSHIP: Record<
  WhiteboardEdgeKind,
  { type: RelationshipEdgeType; directionality: RelationshipDirectionality }
> = {
  arrow: { type: 'dependency', directionality: 'directed' },
  dashed_arrow: { type: 'prerequisite', directionality: 'directed' },
  double_arrow: { type: 'related', directionality: 'undirected' },
  line: { type: 'related', directionality: 'undirected' },
  dashed_line: { type: 'related', directionality: 'undirected' },
  other: { type: 'related', directionality: 'undirected' },
};

/**
 * Symbol / short word written ON a connector → relationship type. This is the
 * arrow-side analog of DEFAULT_COLOR_TO_CATEGORY: when a recognizable marker is
 * present it wins over the line-style default below, so a plain line labeled
 * "blocks" still becomes a dependency. Tokens are matched case-insensitively;
 * multi-word tokens match as substrings, single words as whole tokens, and
 * glyphs by literal containment.
 */
const SYMBOL_RULES: Array<{
  tokens: string[];
  type: RelationshipEdgeType;
  directionality: RelationshipDirectionality;
}> = [
  { tokens: ['contains', 'includes', 'has', '⊃', '⊇'], type: 'contains', directionality: 'directed' },
  { tokens: ['blocks', 'blocked by', 'depends on', 'depends', 'dependency', 'dep', '⊗'], type: 'dependency', directionality: 'directed' },
  { tokens: ['needs', 'requires', 'require', 'req', 'prereq', 'prerequisite', 'before'], type: 'prerequisite', directionality: 'directed' },
  { tokens: ['breaks down', 'breakdown', 'splits into', 'splits'], type: 'breakdown', directionality: 'directed' },
  { tokens: ['elaborates', 'elaboration', 'details', 'example', 'eg'], type: 'elaboration', directionality: 'directed' },
  { tokens: ['aggregates', 'aggregation', 'rolls up', 'sum of'], type: 'aggregation', directionality: 'directed' },
  { tokens: ['related', 'relates', 'rel', 'see also', '~', '≈'], type: 'related', directionality: 'undirected' },
];

function normalizeSymbol(s: string): string {
  return s.toLowerCase().replace(/[.]/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Resolve a connector's symbol/word to a relationship type, or null if it
 * doesn't match a known marker. Pure & deterministic.
 */
export function matchSymbolToRelationship(
  raw: string | undefined,
): { type: RelationshipEdgeType; directionality: RelationshipDirectionality } | null {
  if (!raw) return null;
  const norm = normalizeSymbol(raw);
  if (!norm) return null;
  const words = new Set(norm.split(' '));

  for (const rule of SYMBOL_RULES) {
    for (const tok of rule.tokens) {
      const isGlyph = /[^\p{L}\p{N}\s]/u.test(tok);
      if (isGlyph) {
        if (raw.includes(tok)) return { type: rule.type, directionality: rule.directionality };
        continue;
      }
      if (tok.includes(' ')) {
        if (norm.includes(tok)) return { type: rule.type, directionality: rule.directionality };
      } else if (words.has(tok)) {
        return { type: rule.type, directionality: rule.directionality };
      }
    }
  }
  return null;
}

/** Human-readable legend for the preview UI. */
export const SIGNIFIER_LEGEND: Array<{ signifier: string; mapsTo: string }> = [
  { signifier: 'Box / sticky / circle', mapsTo: 'A new Item (text → title)' },
  { signifier: 'Shape color', mapsTo: 'Item attribute via Field Mappings visual language (e.g. red → risk)' },
  { signifier: 'Shape', mapsTo: 'Item attribute (e.g. diamond → category risk)' },
  { signifier: 'Vertical position', mapsTo: 'Altitude (top = strategic, bottom = tactical)' },
  { signifier: 'Arrow A → B', mapsTo: 'Sequence & conditions: A must be addressed before B (stored as dependency)' },
  { signifier: 'Dashed arrow A → B', mapsTo: 'Sequence & conditions: A before B (stored as prerequisite)' },
  { signifier: 'Line / arrow color or shape', mapsTo: 'Relationship type when mapped in Field Mappings' },
  { signifier: 'Symbol on arrow', mapsTo: '"blocks"→sequence, "needs"→sequence, "⊃"→contains…' },
  { signifier: 'Box drawn around others', mapsTo: '"Contains" relationship' },
];

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/**
 * Transcriptions below this confidence are flagged for human review before
 * import — handwriting OCR is the least reliable signal in a board photo.
 */
export const LOW_CONFIDENCE_THRESHOLD = 0.6;

// ============================================================================
// Duplicate detection (re-import safety)
// ============================================================================
//
// A whiteboard has no stable external id the way a Miro sticky does, so we
// match by title against items the workspace already has. This makes re-import
// idempotent-ish: photographing a board a second time (after the team added a
// few arrows) reuses the existing items instead of cloning them — and the new
// connectors still land as relationships between the real items.

export interface ExistingItemRef {
  id: string;
  title: string;
}

export type DuplicateConfidence = 'exact' | 'likely';

export interface DuplicateMatch {
  existingItemId: string;
  existingTitle: string;
  confidence: DuplicateConfidence;
}

/** Lowercase, strip punctuation/diacritics, collapse whitespace. */
function normalizeTitle(s: string): string {
  return s
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenSet(s: string): Set<string> {
  return new Set(normalizeTitle(s).split(' ').filter(Boolean));
}

function jaccard(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let intersection = 0;
  for (const t of a) if (b.has(t)) intersection++;
  return intersection / (a.size + b.size - intersection);
}

/** Token-overlap threshold above which a non-exact title is flagged as a likely dup. */
const LIKELY_DUPLICATE_THRESHOLD = 0.6;

/**
 * Find the best existing-item match for a title. Exact (normalized) matches win
 * immediately; otherwise the highest token-Jaccard score above the threshold is
 * returned as a "likely" match. Pure & deterministic for testing.
 */
export function findDuplicateMatch(
  title: string,
  existing: ExistingItemRef[],
): DuplicateMatch | null {
  const target = normalizeTitle(title);
  if (!target) return null;

  const targetTokens = tokenSet(title);
  let best: { ref: ExistingItemRef; score: number } | null = null;

  for (const candidate of existing) {
    const norm = normalizeTitle(candidate.title);
    if (!norm) continue;
    if (norm === target) {
      return { existingItemId: candidate.id, existingTitle: candidate.title, confidence: 'exact' };
    }
    const score = jaccard(targetTokens, tokenSet(candidate.title));
    if (!best || score > best.score) best = { ref: candidate, score };
  }

  if (best && best.score >= LIKELY_DUPLICATE_THRESHOLD) {
    return { existingItemId: best.ref.id, existingTitle: best.ref.title, confidence: 'likely' };
  }
  return null;
}

/**
 * Spatial → attribute mapping: a shape's height on the board implies its
 * altitude. Boards are conventionally drawn with strategy/outcomes near the top
 * and concrete tasks toward the bottom. y is normalized (0 = top, 1 = bottom).
 */
export function altitudeFromPosition(y: number): AltitudeLevel {
  const level = Math.round((1 - clamp(y, 0, 1)) * 4) + 1;
  return clamp(level, 1, 5) as AltitudeLevel;
}

// ============================================================================
// Import plan — the editable bridge between scene and platform writes
// ============================================================================

export interface PlannedItem {
  /** Scene node id this item came from. */
  ref: string;
  /** Pre-generated platform item id, so relationships can reference it. */
  itemId: string;
  title: string;
  /** The title as first recognized by the vision model, before any user edit. */
  recognizedTitle: string;
  description?: string;
  itemCategory?: ItemLanguageType;
  altitudeLevel?: AltitudeLevel;
  /** Attribute values assigned by the workspace's visual-language rules (e.g. impact→10). */
  metadata?: Record<string, string | number>;
  color: string;
  shape: WhiteboardNodeShape;
  position: { x: number; y: number };
  /** Model confidence (0–1) in the recognized text. */
  confidence: number;
  /** True when confidence is low and the user should check the title before import. */
  needsReview: boolean;
  /** Whether to process this node at all on confirm. */
  include: boolean;
  /** Create a new item vs. reuse a detected duplicate. */
  resolution: ItemResolution;
  /** Existing item this node appears to duplicate, if any. */
  duplicateOf?: DuplicateMatch;
  /** Corner anchor code written on the physical shape (exact re-identification key). */
  code?: string;
}

/**
 * What to do with a planned item on confirm:
 * - 'create'       → create a new platform item
 * - 'use_existing' → don't create; reuse a matched existing item so any
 *                    relationships drawn to this node attach to the real item.
 */
export type ItemResolution = 'create' | 'use_existing';

export interface PlannedRelationship {
  ref: string;
  sourceRef: string;
  targetRef: string;
  type: RelationshipEdgeType;
  directionality: RelationshipDirectionality;
  label?: string;
  /** The connector marker that determined the type, when one was recognized. */
  symbol?: string;
  /** True when the type came from a symbol/word on the arrow vs. its line style. */
  fromSymbol: boolean;
  /** 'contains' relationships derived from a surrounding box rather than a connector. */
  fromContainment: boolean;
  /** 0–1 confidence in the type inference, with the basis that produced it. */
  confidence: number;
  basis: RelationshipInferenceBasis;
  include: boolean;
}

export interface WhiteboardImportPlan {
  title?: string;
  summary?: string;
  items: PlannedItem[];
  relationships: PlannedRelationship[];
  warnings: string[];
  /** Marks the model couldn't classify — surfaced for the user to act on. */
  unrecognized: WhiteboardUnrecognized[];
  /** Lanes / quadrants / legend boxes recognized on the board. */
  regions: WhiteboardRegion[];
}

export interface BuildPlanOptions {
  /** Apply vertical-position → altitude mapping. Default true. */
  mapAltitudeFromPosition?: boolean;
  /** Apply color → category mapping. Default true. */
  mapColorToCategory?: boolean;
  /** Override / extend the default color map. */
  colorToCategory?: Record<string, ItemLanguageType>;
  /**
   * The workspace's visual-language rules (shared with Miro). When present, a
   * shape's cues (fill/border/text color, shape) are mapped to item attributes
   * and take precedence over the default color→category / position→altitude
   * heuristics where they assign a value.
   */
  signifierRules?: MiroColorRule[];
  /**
   * Existing workspace items to check each node against for duplicates. Exact
   * title matches default to 'use_existing'; likely matches are flagged but
   * default to 'create' so the user decides.
   */
  existingItems?: ExistingItemRef[];
}

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `wb-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

/**
 * Turn a recognized scene into an editable plan of items + relationships,
 * applying the default signifier mappings. Pure & deterministic so it can be
 * unit-tested and re-run when the user toggles mapping options in the preview.
 */
export function buildImportPlan(
  scene: WhiteboardScene,
  options: BuildPlanOptions = {},
): WhiteboardImportPlan {
  const {
    mapAltitudeFromPosition = true,
    mapColorToCategory = true,
    colorToCategory = DEFAULT_COLOR_TO_CATEGORY,
    signifierRules = [],
    existingItems = [],
  } = options;

  const refToItemId = new Map<string, string>();

  const items: PlannedItem[] = scene.nodes
    .filter((n) => n.text.trim().length > 0 || !n.isContainer)
    .map((node) => {
      const itemId = newId();
      refToItemId.set(node.id, itemId);

      const lines = node.text.split('\n').map((l) => l.trim()).filter(Boolean);
      const title = (lines[0] || node.text.trim() || 'Untitled').slice(0, 200);
      const description = lines.length > 1 ? lines.slice(1).join('\n') : undefined;

      const color = (node.color || 'unknown').toLowerCase();

      // The workspace's visual language wins where it assigns a value; otherwise
      // fall back to the default color→category and position→altitude heuristics.
      const patch = signifierRules.length
        ? resolveSignifierPatch(
            { fillColor: node.fillColor ?? color, borderColor: node.borderColor, textColor: node.textColor, shape: node.shape },
            signifierRules,
          )
        : null;
      const itemCategory = patch?.itemCategory ?? (mapColorToCategory ? colorToCategory[color] : undefined);
      const altitudeLevel =
        patch?.altitudeLevel ?? (mapAltitudeFromPosition ? altitudeFromPosition(node.position?.y ?? 0.5) : undefined);

      const duplicateOf = existingItems.length > 0 ? findDuplicateMatch(title, existingItems) : null;
      const confidence = clamp(node.confidence ?? 1, 0, 1);

      // Region membership is spatial meaning: the lane/quadrant title travels
      // on the item as metadata (regions themselves never become items).
      const region = node.regionId ? (scene.regions ?? []).find((r) => r.id === node.regionId) : undefined;
      const regionMeta: Record<string, string> = {};
      if (region?.title) {
        if (region.kind === 'quadrant') regionMeta.quadrant = region.title;
        else if (region.kind !== 'legend') regionMeta.lane = region.title;
      }

      return {
        ref: node.id,
        itemId,
        title,
        recognizedTitle: title,
        description,
        confidence,
        needsReview: confidence < LOW_CONFIDENCE_THRESHOLD,
        itemCategory,
        altitudeLevel,
        ...((patch?.metadata && Object.keys(patch.metadata).length) || Object.keys(regionMeta).length
          ? { metadata: { ...regionMeta, ...(patch?.metadata ?? {}) } }
          : {}),
        color,
        shape: node.shape,
        position: node.position ?? { x: 0.5, y: 0.5 },
        ...(node.code ? { code: node.code } : {}),
        include: true,
        // Exact dups default to reusing the real item; likely dups stay 'create'
        // (flagged) so the user makes the call.
        resolution: duplicateOf?.confidence === 'exact' ? 'use_existing' : 'create',
        ...(duplicateOf && { duplicateOf }),
      };
    });

  const relationships: PlannedRelationship[] = extractSceneRelationships(scene, signifierRules)
    .filter((r) => refToItemId.has(r.sourceNodeId) && refToItemId.has(r.targetNodeId))
    .map((r) => ({
      ref: newId(),
      sourceRef: r.sourceNodeId,
      targetRef: r.targetNodeId,
      type: r.type,
      directionality: r.directionality,
      label: r.label,
      symbol: r.symbol,
      fromSymbol: r.fromSymbol,
      fromContainment: r.fromContainment,
      confidence: r.confidence,
      basis: r.basis,
      include: true,
    }));

  const warnings = [...(scene.warnings ?? [])];
  if ((scene.regions ?? []).some((r) => r.kind === 'legend')) {
    warnings.push(
      'The board has a legend/key box — check Settings → Field Mappings visual rules match what it declares.',
    );
  }

  return {
    title: scene.title,
    summary: scene.summary,
    items,
    relationships,
    warnings,
    unrecognized: scene.unrecognized ?? [],
    regions: scene.regions ?? [],
  };
}

/** A relationship read out of a scene, still keyed by scene node ids. */
/** How an inferred relationship's type was determined (uncertainty is recorded,
 * never resolved silently — semantic-connectors 02-DESIGN §5). */
export type RelationshipInferenceBasis = 'symbol' | 'visual_language' | 'spatial';

export interface SceneRelationship {
  sourceNodeId: string;
  targetNodeId: string;
  type: RelationshipEdgeType;
  directionality: RelationshipDirectionality;
  label?: string;
  symbol?: string;
  fromSymbol: boolean;
  fromContainment: boolean;
  /** 0–1 confidence in the TYPE inference (not the drawing's existence). */
  confidence: number;
  basis: RelationshipInferenceBasis;
}

const VALID_REL_TYPES: ReadonlySet<string> = new Set<RelationshipEdgeType>([
  'dependency', 'prerequisite', 'related', 'contains', 'breakdown', 'elaboration', 'aggregation',
]);

/**
 * Visual arrow A → B means A precedes B on the board (A must be addressed
 * before B). Sequence & conditions stores that meaning with two mirror types
 * (canon Doc 12 §2.5):
 *   - dependency:   source = dependent, target = prerequisite  → store B → A
 *   - prerequisite: source = prerequisite, target = dependent → store A → B
 *
 * Symbols that mean "the arrow source depends on / needs the target" keep
 * visual endpoints for dependency and flip for prerequisite.
 */
export function orientWhiteboardSequenceEndpoints(
  visualSourceId: string,
  visualTargetId: string,
  type: RelationshipEdgeType,
  symbol?: string,
): { sourceId: string; targetId: string } {
  if (type !== 'dependency' && type !== 'prerequisite') {
    return { sourceId: visualSourceId, targetId: visualTargetId };
  }

  const n = symbol ? normalizeSymbol(symbol) : '';
  const sourceIsDependent =
    /\b(depends|dependency|dep|blocked by|needs|requires|require|req|prereq|prerequisite)\b/.test(n)
    || n === 'depends on';

  if (type === 'dependency') {
    // Default / "blocks": visual source is predecessor → flip into dependent→prereq.
    // "depends on" / "needs": visual source is already the dependent → keep.
    if (sourceIsDependent) return { sourceId: visualSourceId, targetId: visualTargetId };
    return { sourceId: visualTargetId, targetId: visualSourceId };
  }

  // prerequisite: default / "before": visual source is predecessor → keep.
  // "needs" / "requires": visual source is dependent → flip into prereq→dependent.
  if (sourceIsDependent) return { sourceId: visualTargetId, targetId: visualSourceId };
  return { sourceId: visualSourceId, targetId: visualTargetId };
}

/** Derive the six Field-Mappings connector channels from an edge. */
export function edgeSignifierTokens(edge: Pick<WhiteboardEdge, 'kind' | 'color'>): {
  line_style: WhiteboardEdgeKind;
  line_type: 'solid' | 'dashed';
  arrow_shape: 'none' | 'single' | 'double';
  line_color?: string;
  arrow_color?: string;
} {
  const kind = edge.kind;
  const isDashed = kind === 'dashed_arrow' || kind === 'dashed_line';
  const isArrow = kind === 'arrow' || kind === 'double_arrow' || kind === 'dashed_arrow';
  const arrow_shape: 'none' | 'single' | 'double' =
    kind === 'double_arrow' ? 'double'
      : isArrow ? 'single'
        : 'none';
  const color = edge.color?.toLowerCase().trim();
  return {
    line_style: kind,
    line_type: isDashed ? 'dashed' : 'solid',
    arrow_shape,
    ...(color && color !== 'unknown'
      ? (isArrow ? { arrow_color: color } : { line_color: color })
      : {}),
  };
}

/**
 * A user's connector visual-language rules (line_style, line_type, arrow_shape,
 * line_color, arrow_color) let a team define their own connector language
 * (e.g. "double arrow = aggregation", "red arrow → dependency"). Returns the
 * relationship type a given edge maps to under the rules, or null when none
 * matches. Legacy `line_style` rules still win when present.
 */
export function matchLineStyleRule(
  kind: WhiteboardEdgeKind,
  rules: MiroColorRule[],
  edgeColor?: string,
): RelationshipEdgeType | null {
  const tokens = edgeSignifierTokens({ kind, color: edgeColor });
  for (const rule of rules) {
    const dim = rule.dimension;
    if (!dim || dim === 'fill_color' || dim === 'shape_color' || dim === 'border_color'
      || dim === 'text_color' || dim === 'shape') {
      continue;
    }
    const observed =
      dim === 'line_style' ? tokens.line_style
        : dim === 'line_type' ? tokens.line_type
          : dim === 'arrow_shape' ? tokens.arrow_shape
            : dim === 'line_color' ? tokens.line_color
              : dim === 'arrow_color' ? tokens.arrow_color
                : undefined;
    if (!observed) continue;
    if (rule.color.toLowerCase().trim() !== observed) continue;
    if (rule.value && VALID_REL_TYPES.has(rule.value)) return rule.value as RelationshipEdgeType;
  }
  return null;
}

/**
 * Read every relationship a scene implies — connectors (with symbol/line-style
 * typing) and containment ("box drawn around others") — keyed by node id. Pure;
 * shared by buildImportPlan and the version diff/merge engine so both agree on
 * how a drawing maps to relationship types. `lineStyleRules` (the workspace's
 * visual language) override the default line-style → type mapping.
 */
export function extractSceneRelationships(
  scene: WhiteboardScene,
  lineStyleRules: MiroColorRule[] = [],
): SceneRelationship[] {
  const nodeIds = new Set(scene.nodes.map((n) => n.id));
  const rels: SceneRelationship[] = [];

  for (const edge of scene.edges) {
    if (!nodeIds.has(edge.sourceId) || !nodeIds.has(edge.targetId)) continue;
    if (edge.sourceId === edge.targetId) continue;
    // A symbol/word on the connector wins over everything. Only treat a short
    // label as a symbol candidate so long descriptions that happen to contain a
    // keyword stay pure annotations.
    const labelText = edge.label?.trim();
    const shortLabel = labelText && labelText.split(/\s+/).length <= 3 ? labelText : undefined;
    const symbolCandidate = edge.symbol?.trim() || shortLabel;
    const symbolMatch = matchSymbolToRelationship(symbolCandidate);
    const styleMapping = DEFAULT_EDGE_TO_RELATIONSHIP[edge.kind] ?? DEFAULT_EDGE_TO_RELATIONSHIP.other;
    // Precedence: explicit symbol → user line-style rule → built-in default.
    // A user rule overrides the semantic type but keeps the kind's natural
    // direction (an arrow stays directed, a plain line undirected).
    const lineStyleType = matchLineStyleRule(edge.kind, lineStyleRules, edge.color);
    const mapping = symbolMatch ?? (lineStyleType ? { type: lineStyleType, directionality: styleMapping.directionality } : styleMapping);

    // Record how sure we are and why: a written symbol is near-explicit; a
    // workspace line-style rule is the team's own convention; the built-in
    // style default is the weakest signal.
    const confidence = symbolMatch ? 0.9 : lineStyleType ? 0.8 : 0.7;
    const basis: RelationshipInferenceBasis = symbolMatch ? 'symbol' : 'visual_language';

    // Orient sequence endpoints for Sequence & conditions (dependency /
    // prerequisite mirrors). Visual A→B = A precedes B.
    const oriented = orientWhiteboardSequenceEndpoints(
      edge.sourceId,
      edge.targetId,
      mapping.type,
      symbolMatch ? symbolCandidate : undefined,
    );

    rels.push({
      sourceNodeId: oriented.sourceId,
      targetNodeId: oriented.targetId,
      type: mapping.type,
      directionality: mapping.directionality,
      label: labelText || undefined,
      symbol: symbolMatch ? symbolCandidate : undefined,
      fromSymbol: !!symbolMatch,
      fromContainment: false,
      confidence,
      basis,
    });
  }

  for (const node of scene.nodes) {
    if (!node.containerId) continue;
    if (!nodeIds.has(node.containerId) || node.containerId === node.id) continue;
    rels.push({
      sourceNodeId: node.containerId,
      targetNodeId: node.id,
      type: 'contains',
      directionality: 'directed',
      fromSymbol: false,
      fromContainment: true,
      // A drawn enclosure is visible but its MEANING is inferred spatially.
      confidence: 0.75,
      basis: 'spatial',
    });
  }

  return rels;
}

// ============================================================================
// Provenance
// ============================================================================
//
// Imported items should be traceable back to where they came from on the
// board. The capture photos ARE stored (whiteboardCaptureStore → the
// whiteboard-captures bucket, migration 535) as visual provenance; this module
// additionally captures each item's structured origin — board, recognized-vs-
// edited text, position, color, shape — as a history event that surfaces on
// the item's Activity timeline. Pure & deterministic so the phrasing is testable.

function verticalBand(y: number): string {
  return y < 0.34 ? 'top' : y > 0.66 ? 'bottom' : 'middle';
}
function horizontalBand(x: number): string {
  return x < 0.34 ? 'left' : x > 0.66 ? 'right' : 'center';
}

/** Human phrase for a normalized board position, e.g. "top-left", "center". */
export function describePosition(pos: { x: number; y: number }): string {
  const v = verticalBand(clamp(pos.y, 0, 1));
  const h = horizontalBand(clamp(pos.x, 0, 1));
  if (v === 'middle' && h === 'center') return 'center';
  if (v === 'middle') return h;
  if (h === 'center') return v;
  return `${v}-${h}`;
}

export interface ProvenanceInput {
  boardTitle?: string;
  recognizedText: string;
  finalTitle: string;
  color: string;
  shape: WhiteboardNodeShape;
  position: { x: number; y: number };
  /** True when the node was linked to an existing item rather than created. */
  reused: boolean;
  matchedTitle?: string;
  /** Model confidence (0–1) in the recognized text. */
  confidence?: number;
}

export interface ItemProvenance {
  note: string;
  details: Record<string, unknown>;
}

/** Build the human note + structured detail recorded against an imported item. */
export function buildItemProvenance(input: ProvenanceInput): ItemProvenance {
  const board = input.boardTitle?.trim();
  const where = describePosition(input.position);
  const colorPart = input.color && input.color !== 'unknown' ? `${input.color.replace(/_/g, ' ')} ` : '';
  const shapePart = input.shape === 'text' ? 'note' : input.shape.replace(/_/g, ' ');
  const recognized = input.recognizedText.trim();
  const finalTitle = input.finalTitle.trim();
  const edited = recognized.length > 0 && recognized !== finalTitle;

  let note = `Imported from whiteboard${board ? ` “${board}”` : ''}. `;
  note += `Recognized as a ${colorPart}${shapePart} in the ${where}`;
  note += recognized ? `; transcribed “${recognized}”.` : '.';
  if (edited) note += ` Edited to “${finalTitle}”.`;
  if (input.reused && input.matchedTitle) {
    note += ` Linked to existing item “${input.matchedTitle}” instead of creating a duplicate.`;
  }

  return {
    note,
    details: {
      source: 'whiteboard_photo',
      boardTitle: board ?? null,
      recognizedText: recognized,
      finalTitle,
      edited,
      color: input.color,
      shape: input.shape,
      position: input.position,
      reused: input.reused,
      matchedTitle: input.matchedTitle ?? null,
      confidence: input.confidence ?? null,
    },
  };
}

// ============================================================================
// Vision analysis
// ============================================================================

/**
 * Vision-capable Claude model used to read the whiteboard photo. On our own key
 * the server pins the same model (api/_lib/managedWhiteboard.js).
 * Sonnet 5.5 rejects a non-default temperature, so none is sent.
 */
export const VISION_MODEL = 'claude-sonnet-5-5';

export const ANALYSIS_SYSTEM_PROMPT = `You are a computer-vision parser for photos of physical whiteboards, flip charts, and sticky-note walls. You extract the visual grammar people draw and return it as a strict JSON scene graph. You never invent content that is not visibly present.

Recognize two kinds of things:
1. NODES — discrete shapes that carry an idea: sticky notes, drawn boxes/rectangles, rounded boxes, circles/ovals, diamonds, clouds, and standalone blocks of text. Transcribe the handwriting as faithfully as you can. If a larger box is drawn AROUND a group of other shapes, mark it as a container (isContainer: true) and set containerId on the shapes inside it.
2. EDGES — connectors drawn between nodes: arrows (single-headed), double_arrow (two heads), dashed_arrow, line (plain), dashed_line. For arrows, source is the tail and target is the head. If a SHORT marker or word is written ON the connector to indicate the KIND of relationship — e.g. "⊃", "contains", "blocks", "depends", "needs", "requires", "breaks down", "related" — put that in "symbol". Put any other longer descriptive text in "label". Also report the connector's stroke "color" using the same color vocabulary as nodes (use "unknown" for ordinary black/dark marker on a white board). Colored pens on connectors matter — teams map line color and arrow color to relationship meaning.

For every node, estimate a normalized position with origin at the TOP-LEFT of the board: x and y each in [0,1] (x = left→right, y = top→bottom), using the visual CENTER of the shape. Estimate the dominant color as one of: yellow, orange, red, pink, green, light_green, blue, light_blue, cyan, purple, violet, gray, white, unknown. Use "unknown" for plain marker ink on a white board.

A team may encode meaning in SEPARATE color channels, so report each one you can distinguish, using the same color vocabulary: "fillColor" (the shape's fill/sticky color), "borderColor" (the outline/border color, if drawn in a different color), and "textColor" (the handwriting/marker color of the text). If a channel isn't clearly distinguishable, set it to the dominant "color" or "unknown". These let a user map, say, fill→impact, border→risk, and text color→team.

For every node also give a "confidence" between 0 and 1 for how sure you are of the TEXT transcription: 1.0 = crisp printed/clear text; ~0.5 = legible but ambiguous handwriting or partial glare; below 0.4 = guessing, messy, or partly obscured. Be honest — low confidence triggers human review rather than silently importing a wrong transcription.

If a SHORT anchor code (1–4 characters, e.g. "K7", "B3", "AC") is written in a CORNER of a shape — distinct from the shape's main text — capture it in "code" (uppercase, no spaces). These are identity tags written by the team; omit "code" when no such corner tag is present. Do NOT treat the shape's regular text as a code.

REGIONS: boards often have STRUCTURE drawn as large areas rather than idea-shapes: horizontal or vertical SWIMLANES (long ruled bands, usually with a written lane title at the edge), a 2×2 MATRIX whose four cells are QUADRANTS (axis labels or quadrant names written in/near each cell), or a LEGEND/KEY box (a small area listing what colors/shapes mean). Report each as a region with kind "lane_horizontal", "lane_vertical", "quadrant", or "legend", its written title when present, and its normalized bounds (x, y = top-left corner; width, height in [0,1]). For every NODE whose center sits inside a lane or quadrant, set its "regionId" to that region's id. Do NOT create nodes for lane titles, axis labels, or legend contents — they belong to the region. A container box drawn tightly around a small group of shapes is still a container (isContainer), not a region; regions are the large structural divisions of the board.

REGISTRATION MARKS: the board may have four L-shaped (right-angle) corner marks projected or drawn near its outer corners — one bracket at each corner, opening toward the center. If you can clearly see all FOUR, report each one's photo position (normalized 0–1, with origin at the photo's top-left) in "fiducials". Use the corner of the L (the vertex where the two strokes meet) as the point. Omit "fiducials" entirely unless you can locate all four marks.

Return ONLY a JSON object, no prose, no markdown fences, matching exactly:
{
  "title": string,            // a short title for the board, inferred from its content
  "summary": string,          // one sentence describing what the board depicts
  "nodes": [
    { "id": "n1", "text": "...", "shape": "sticky|box|rounded_box|circle|diamond|cloud|text|other",
      "color": "...", "fillColor": "...", "borderColor": "...", "textColor": "...", "x": 0.0, "y": 0.0, "confidence": 0.0, "code": "", "isContainer": false, "containerId": null, "regionId": null }
  ],
  "regions": [                // OPTIONAL — lanes / quadrants / legend boxes (structure, not ideas)
    { "id": "r1", "kind": "lane_horizontal|lane_vertical|quadrant|legend", "title": "", "x": 0.0, "y": 0.0, "width": 0.0, "height": 0.0 }
  ],
  "edges": [
    { "id": "e1", "source": "n1", "target": "n2", "kind": "arrow|double_arrow|dashed_arrow|line|dashed_line|other", "color": "unknown|red|blue|…", "symbol": "", "label": "" }
  ],
  "fiducials": {              // OPTIONAL — only when all four L-shaped corner marks are visible
    "topLeft": { "x": 0.0, "y": 0.0 }, "topRight": { "x": 0.0, "y": 0.0 },
    "bottomRight": { "x": 0.0, "y": 0.0 }, "bottomLeft": { "x": 0.0, "y": 0.0 }
  },
  "unrecognized": [           // marks you CANNOT confidently classify as a node or connector
    { "id": "u1", "description": "scribble", "kind": "shape|connector|text|other", "x": 0.0, "y": 0.0 }
  ],
  "warnings": [ "..." ]        // e.g. unreadable handwriting, glare, ambiguous arrows
}
If you see marks, scribbles, doodles, or regions you CANNOT confidently turn into a node or a connector — an ambiguous squiggle, text too illegible to transcribe, or an arrow whose endpoints are unclear — do NOT force them into "nodes" or "edges". Instead list each in "unrecognized" with a short "description", a best-guess "kind", and an approximate position. Leave "unrecognized" empty when everything was classifiable.
Node ids must be unique. Edge source/target MUST reference existing node ids. If the image is not a whiteboard/diagram, return empty nodes and a warning.`;

interface RawNode {
  id?: string;
  text?: string;
  shape?: string;
  color?: string;
  fillColor?: string;
  borderColor?: string;
  textColor?: string;
  x?: number;
  y?: number;
  confidence?: number;
  code?: string;
  isContainer?: boolean;
  containerId?: string | null;
  regionId?: string | null;
}

interface RawRegion {
  id?: string;
  kind?: string;
  title?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

interface RawEdge {
  id?: string;
  source?: string;
  target?: string;
  kind?: string;
  color?: string;
  label?: string;
  symbol?: string;
}

const VALID_SHAPES: WhiteboardNodeShape[] = [
  'sticky', 'box', 'rounded_box', 'circle', 'diamond', 'cloud', 'text', 'other',
];
const VALID_EDGE_KINDS: WhiteboardEdgeKind[] = [
  'arrow', 'double_arrow', 'dashed_arrow', 'line', 'dashed_line', 'other',
];

function coerceShape(s: unknown): WhiteboardNodeShape {
  return VALID_SHAPES.includes(s as WhiteboardNodeShape) ? (s as WhiteboardNodeShape) : 'other';
}
function coerceEdgeKind(k: unknown): WhiteboardEdgeKind {
  return VALID_EDGE_KINDS.includes(k as WhiteboardEdgeKind) ? (k as WhiteboardEdgeKind) : 'other';
}

/** Strip markdown fences and parse the first JSON object found in the text. */
export function parseSceneJson(text: string): WhiteboardScene {
  let cleaned = text.trim();
  // Remove ```json … ``` fences if the model added them despite instructions.
  const fence = cleaned.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence) cleaned = fence[1].trim();
  // Fall back to the outermost braces.
  if (!cleaned.startsWith('{')) {
    const start = cleaned.indexOf('{');
    const end = cleaned.lastIndexOf('}');
    if (start >= 0 && end > start) cleaned = cleaned.slice(start, end + 1);
  }

  let raw: {
    title?: string;
    summary?: string;
    nodes?: RawNode[];
    edges?: RawEdge[];
    warnings?: string[];
    unrecognized?: Array<{ id?: unknown; description?: unknown; kind?: unknown; x?: unknown; y?: unknown }>;
    regions?: RawRegion[];
    fiducials?: Record<string, { x?: unknown; y?: unknown } | undefined>;
  };
  try {
    raw = JSON.parse(cleaned);
  } catch {
    throw new Error('The vision model returned a response we could not parse. Try a clearer, well-lit photo.');
  }

  const nodes: WhiteboardNode[] = (raw.nodes ?? [])
    .filter((n) => n && typeof n.id === 'string')
    .map((n) => ({
      id: String(n.id),
      text: typeof n.text === 'string' ? n.text : '',
      shape: coerceShape(n.shape),
      color: typeof n.color === 'string' && n.color ? n.color.toLowerCase() : 'unknown',
      ...(typeof n.fillColor === 'string' && n.fillColor ? { fillColor: n.fillColor.toLowerCase() } : {}),
      ...(typeof n.borderColor === 'string' && n.borderColor ? { borderColor: n.borderColor.toLowerCase() } : {}),
      ...(typeof n.textColor === 'string' && n.textColor ? { textColor: n.textColor.toLowerCase() } : {}),
      position: {
        x: clamp(Number.isFinite(n.x) ? Number(n.x) : 0.5, 0, 1),
        y: clamp(Number.isFinite(n.y) ? Number(n.y) : 0.5, 0, 1),
      },
      confidence: Number.isFinite(n.confidence) ? clamp(Number(n.confidence), 0, 1) : 1,
      ...(typeof n.code === 'string' && n.code.trim()
        ? { code: n.code.trim().toUpperCase().replace(/\s+/g, '').slice(0, 6) }
        : {}),
      isContainer: Boolean(n.isContainer),
      containerId: n.containerId ? String(n.containerId) : null,
      regionId: n.regionId ? String(n.regionId) : null,
    }));

  const nodeIds = new Set(nodes.map((n) => n.id));
  const edges: WhiteboardEdge[] = (raw.edges ?? [])
    .filter((e) => e && nodeIds.has(String(e.source)) && nodeIds.has(String(e.target)))
    .map((e) => ({
      id: typeof e.id === 'string' && e.id ? e.id : newId(),
      sourceId: String(e.source),
      targetId: String(e.target),
      kind: coerceEdgeKind(e.kind),
      ...(typeof e.color === 'string' && e.color.trim()
        ? { color: e.color.toLowerCase().trim() }
        : {}),
      label: typeof e.label === 'string' ? e.label : undefined,
      symbol: typeof e.symbol === 'string' ? e.symbol : undefined,
    }));

  // Drop containerId references that point at nodes we dropped.
  for (const node of nodes) {
    if (node.containerId && !nodeIds.has(node.containerId)) node.containerId = null;
  }

  const regions = parseRegions(raw.regions);
  const regionIds = new Set(regions.map((r) => r.id));
  for (const node of nodes) {
    if (node.regionId && !regionIds.has(node.regionId)) node.regionId = null;
  }

  return {
    title: typeof raw.title === 'string' ? raw.title : undefined,
    summary: typeof raw.summary === 'string' ? raw.summary : undefined,
    nodes,
    edges,
    warnings: Array.isArray(raw.warnings) ? raw.warnings.filter((w): w is string => typeof w === 'string') : [],
    unrecognized: parseUnrecognized(raw.unrecognized),
    ...(regions.length > 0 ? { regions } : {}),
    ...(parseFiducials(raw.fiducials) ? { fiducials: parseFiducials(raw.fiducials)! } : {}),
  };
}

const VALID_REGION_KINDS = new Set(['lane_horizontal', 'lane_vertical', 'quadrant', 'legend']);

/** Parse board regions (lanes/quadrants/legend), dropping malformed entries. */
function parseRegions(raw: RawRegion[] | undefined): WhiteboardRegion[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r) => r && typeof r.kind === 'string' && VALID_REGION_KINDS.has(r.kind))
    .map((r, i) => ({
      id: typeof r.id === 'string' && r.id ? r.id : `r${i + 1}`,
      kind: r.kind as WhiteboardRegionKind,
      ...(typeof r.title === 'string' && r.title.trim() ? { title: r.title.trim().slice(0, 80) } : {}),
      x: clamp(Number.isFinite(r.x) ? Number(r.x) : 0, 0, 1),
      y: clamp(Number.isFinite(r.y) ? Number(r.y) : 0, 0, 1),
      width: clamp(Number.isFinite(r.width) ? Number(r.width) : 0, 0, 1),
      height: clamp(Number.isFinite(r.height) ? Number(r.height) : 0, 0, 1),
    }));
}

const VALID_UNRECOGNIZED_KINDS = new Set(['shape', 'connector', 'text', 'other']);

/** Parse the model's unrecognized marks, dropping anything without a description. */
function parseUnrecognized(
  raw: Array<{ id?: unknown; description?: unknown; kind?: unknown; x?: unknown; y?: unknown }> | undefined,
): WhiteboardUnrecognized[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((u) => u && typeof u.description === 'string' && u.description.trim())
    .map((u, i) => {
      const kind = typeof u.kind === 'string' && VALID_UNRECOGNIZED_KINDS.has(u.kind)
        ? (u.kind as WhiteboardUnrecognized['kind'])
        : 'other';
      const hasPos = Number.isFinite(Number(u.x)) && Number.isFinite(Number(u.y));
      return {
        id: typeof u.id === 'string' && u.id ? u.id : `u${i + 1}`,
        description: String(u.description).trim().slice(0, 120),
        kind,
        ...(hasPos ? { position: { x: clamp(Number(u.x), 0, 1), y: clamp(Number(u.y), 0, 1) } } : {}),
      };
    });
}

/**
 * Parse the four corner registration marks, returning a SceneFiducials only when
 * all four points are present with finite coordinates (a partial set is useless
 * for computing a homography, so it's dropped).
 */
function parseFiducials(
  raw: Record<string, { x?: unknown; y?: unknown } | undefined> | undefined,
): SceneFiducials | undefined {
  if (!raw) return undefined;
  const corner = (key: string): WhiteboardPoint | null => {
    const p = raw[key];
    if (!p || !Number.isFinite(Number(p.x)) || !Number.isFinite(Number(p.y))) return null;
    return { x: clamp(Number(p.x), 0, 1), y: clamp(Number(p.y), 0, 1) };
  };
  const topLeft = corner('topLeft');
  const topRight = corner('topRight');
  const bottomRight = corner('bottomRight');
  const bottomLeft = corner('bottomLeft');
  if (!topLeft || !topRight || !bottomRight || !bottomLeft) return undefined;
  return { topLeft, topRight, bottomRight, bottomLeft };
}

export interface WhiteboardImageInput {
  /** Base64-encoded image data (no data: prefix). */
  base64Data: string;
  /** e.g. "image/jpeg" or "image/png". */
  mediaType: string;
}

/** Split a data URL ("data:image/jpeg;base64,...") into the parts Anthropic needs. */
export function dataUrlToImageInput(dataUrl: string): WhiteboardImageInput {
  const match = dataUrl.match(/^data:(.+?);base64,(.*)$/);
  if (!match) throw new Error('Unsupported image data. Please upload a JPEG or PNG photo.');
  return { mediaType: match[1].split(';')[0].trim().toLowerCase(), base64Data: match[2] };
}

// Anthropic vision accepts JPEG/PNG/GIF/WebP only. iPhone HEIC and some
// drag-drop files fail with HTTP 400 unless we rasterize to JPEG first.
const ANTHROPIC_IMAGE_MEDIA_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);
const MAX_VISION_EDGE_PX = 2048;

function rasterizeDataUrl(
  dataUrl: string,
  outputType: 'image/jpeg' | 'image/png',
): Promise<WhiteboardImageInput> {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Image conversion is only available in the browser.'));
      return;
    }
    const img = new Image();
    img.onload = () => {
      let w = img.naturalWidth;
      let h = img.naturalHeight;
      const maxEdge = Math.max(w, h);
      if (maxEdge > MAX_VISION_EDGE_PX) {
        const scale = MAX_VISION_EDGE_PX / maxEdge;
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not process that image.'));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      const outUrl = canvas.toDataURL(outputType, outputType === 'image/jpeg' ? 0.88 : undefined);
      resolve(dataUrlToImageInput(outUrl));
    };
    img.onerror = () =>
      reject(new Error('Could not read that image. Use JPEG or PNG, or take a photo with the in-app camera.'));
    img.src = dataUrl;
  });
}

/** Normalize a data URL into an Anthropic-acceptable image (HEIC/large → JPEG). */
export async function normalizeWhiteboardImageInput(dataUrl: string): Promise<WhiteboardImageInput> {
  const parsed = dataUrlToImageInput(dataUrl);
  const needsConvert =
    !ANTHROPIC_IMAGE_MEDIA_TYPES.has(parsed.mediaType) || parsed.base64Data.length > 4_500_000;
  return needsConvert ? rasterizeDataUrl(dataUrl, 'image/jpeg') : parsed;
}

function formatVisionApiError(status: number, data: { error?: { message?: string; type?: string } }): string {
  const detail = data?.error?.message || data?.error?.type || `HTTP ${status}`;
  // Managed free-tier quota exhausted — the proxy returns 402. Surface the
  // server's message verbatim (it already tells the user to add their own key).
  if (status === 402 || data?.error?.type === 'quota_exceeded') {
    return data?.error?.message || 'Your workspace has used its whiteboard imports for this month. Add your own Claude API key to keep going.';
  }
  // Refusals from our own key's checks are already plain sentences.
  if (!providerHasApiKey('anthropic') && (status === 401 || status === 403 || status === 503) && data?.error?.message) {
    return data.error.message;
  }
  let message = `Photo analysis failed: ${detail}`;
  const lower = detail.toLowerCase();
  if (lower.includes('credit') || lower.includes('billing') || lower.includes('balance')) {
    const preview = aiKeyPreview('anthropic');
    const source = getAnthropicKeySource();
    const sourceHint =
      source === 'env'
        ? ' The app is using a deployment API key from .env — add your own Claude key under Settings → My Account → My Connectors.'
        : source === 'saved'
          ? ' Check billing for the Claude key you saved under Settings → My Account → My Connectors.'
          : ' Add a Claude API key under Settings → My Account → My Connectors.';
    message += preview ? `${sourceHint} (key ending ${preview})` : sourceHint;
  } else if (lower.includes('media_type') || lower.includes('image source') || lower.includes('image')) {
    message += ' Try JPEG or PNG, or use the Take photo button in the import dialog.';
  } else if (status === 401) {
    message += ' Your Claude API key was rejected — update it under Settings → My Account → My Connectors.';
  }
  return message;
}

type AnthropicContentBlock =
  | { type: 'text'; text: string }
  | { type: 'image'; source: { type: 'base64'; media_type: string; data: string } };

export function buildAnalysisContent(images: WhiteboardImageInput[]): AnthropicContentBlock[] {
  const multi = images.length > 1;
  const content: AnthropicContentBlock[] = [];

  images.forEach((img, i) => {
    if (multi) {
      content.push({
        type: 'text',
        text: `Tile ${i + 1} of ${images.length} (captured left → right along the wall):`,
      });
    }
    content.push({
      type: 'image',
      source: { type: 'base64', media_type: img.mediaType, data: img.base64Data },
    });
  });

  content.push({
    type: 'text',
    text: multi
      ? `These ${images.length} photos are OVERLAPPING sections of ONE larger whiteboard, in left-to-right capture order. Stitch them mentally into a single board and return ONE scene graph: where a shape appears in the overlap between adjacent tiles, emit it as a SINGLE node (never double-count it), and give every node an x in [0,1] across the WHOLE wall (0 = far left of the first tile, 1 = far right of the last tile), y in [0,1] top→bottom. Reconcile connectors that cross tile seams. Otherwise follow the JSON format from the system prompt exactly. Return JSON only.`
      : 'Parse this whiteboard photo into the JSON scene graph described in the system prompt. Return JSON only.',
  });

  return content;
}

/**
 * Send one or more overlapping photos of a single whiteboard to Claude vision
 * and return one merged, de-duplicated scene graph. A wall is captured as
 * several left-to-right tiles ("walk the wall") so handwriting stays legible at
 * full local resolution; the model reconciles overlaps into a single board.
 * Uses the same same-origin Anthropic proxy as the rest of the app.
 */
export async function analyzeWhiteboardPhotos(
  images: WhiteboardImageInput[],
  opts?: { signal?: AbortSignal; workspaceId?: string | null },
): Promise<WhiteboardScene> {
  if (images.length === 0) {
    throw new Error('Add at least one photo to analyze.');
  }
  const ownKey = providerHasApiKey('anthropic');
  const managed = isManagedAnthropicEnabled();
  if (!ownKey && !managed) {
    throw new Error(
      'Whiteboard import needs a Claude API key for photo analysis. Use the workspace Claude key if enabled, or add your own under Settings → My Account → My Connectors.',
    );
  }

  const requestBody = {
    model: VISION_MODEL,
    max_tokens: 8192,
    system: ANALYSIS_SYSTEM_PROMPT,
    messages: [{ role: 'user', content: buildAnalysisContent(images) }],
  };

  let response: Response;
  try {
    if (ownKey) {
      // The person's own key, attached on the server (ai-proxy, migration 982).
      response = await personalAiFetch('anthropic', requestBody, { signal: opts?.signal });
    } else {
      // Our own key: the server checks who is asking (sign-in), that they belong
      // to the workspace, and the workspace's monthly limit (migration 911).
      const { data } = await getSupabaseClient().auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Sign in to import a whiteboard photo.');
      if (!opts?.workspaceId) throw new Error('Open a workspace before importing a whiteboard photo.');
      response = await fetch('/api/anthropic/v1/messages', {
        method: 'POST',
        signal: opts?.signal,
        headers: {
          'Content-Type': 'application/json',
          'anthropic-version': '2023-06-01',
          Authorization: `Bearer ${token}`,
          'x-workspace-id': opts.workspaceId,
        },
        body: JSON.stringify(requestBody),
      });
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    if (err instanceof Error && /^(Sign in|Open a workspace)/.test(err.message)) throw err;
    throw new Error('Could not reach the photo-analysis service. Check your connection and try again.');
  }

  let data: { content?: Array<{ text?: string }>; error?: { message?: string; type?: string } } = {};
  try {
    data = await response.json();
  } catch {
    /* non-JSON */
  }

  if (!response.ok) {
    throw new Error(formatVisionApiError(response.status, data));
  }

  const text = data.content?.map((b) => b?.text ?? '').join('').trim() ?? '';
  if (!text) throw new Error('The vision model returned an empty response. Try again with a clearer photo.');

  return parseSceneJson(text);
}

/**
 * Single-photo convenience wrapper that accepts a raw data URL and normalizes
 * it (HEIC/large → JPEG) before analysis.
 */
export async function analyzeWhiteboardPhoto(
  imageDataUrl: string,
  opts?: { signal?: AbortSignal },
): Promise<WhiteboardScene> {
  const image = await normalizeWhiteboardImageInput(imageDataUrl);
  return analyzeWhiteboardPhotos([image], opts);
}
